/*
  Darwin's Custom Event Bridge

  This file publishes `darwins:*` events via Shopify.analytics.publish(), so the
  custom pixel (sandboxed iframe) can subscribe in the pixel-side data layer.

  - `darwins:calculator_used` (optional): bridged from Digioh GTM events.
  - `darwins:pet_type_selected`: emitted when a pet-type selector is clicked.
  - `darwins:purchase_option_selected`: emitted when subscribe/one-time toggle is clicked.

  Implementation note:
  - This file is intentionally lightweight and defensive. It will silently
    no-op if `Shopify.analytics.publish` is unavailable.
  - The selectors below are intentionally generic and use `data-*` hooks.
    Update the selector values to match your theme markup or add the expected
    data attributes to the relevant elements in Liquid templates.
*/

(function () {
  const hasPublish = () => window?.Shopify?.analytics?.publish;

  function publishDarwinsEvent(eventName, payload) {
    if (!hasPublish()) {
      // eslint-disable-next-line no-console
      console.warn('Shopify.analytics.publish not available; event not sent:', eventName, payload);
      return;
    }

    try {
      window.Shopify.analytics.publish(eventName, payload);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error publishing darwins event', eventName, error);
    }
  }

  function initCustomEventBridge() {
    const analyticsClient = window.analytics || window.Shopify?.analytics;
    if (!analyticsClient || typeof analyticsClient.subscribe !== 'function') return;

    const getEventContext = (event) => {
      if (typeof window.getEventContext === 'function') {
        try {
          return window.getEventContext(event) || {};
        } catch (error) {
          return {};
        }
      }
      return {};
    };

    const getUserId = () => {
      if (typeof window.getUserId === 'function') {
        try {
          return window.getUserId();
        } catch (error) {
          return undefined;
        }
      }
      return undefined;
    };

    const pushGtmEvent = (payload) => {
      if (!window.dataLayer || !Array.isArray(window.dataLayer)) return;
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push(payload);
    };

    analyticsClient.subscribe('darwins:view_item_list', (event) => {
      const listData = event.customData || {};

      pushGtmEvent({
        event: 'view_item_list',
        ...getEventContext(event),
        user_id: getUserId(),
        ecommerce: {
          item_list_id: listData.item_list_id,
          item_list_name: listData.item_list_name,
          items: (listData.items || []).map((item, index) => ({
            item_id: item.item_id,
            item_name: item.item_name,
            price: item.price ? parseFloat(item.price) : undefined,
            index: index,
            item_brand: "Darwin's Natural Pet Products",
          })),
        },
      });
    });

    analyticsClient.subscribe('darwins:select_item', (event) => {
      const itemData = event.customData || {};

      pushGtmEvent({
        event: 'select_item',
        ...getEventContext(event),
        user_id: getUserId(),
        ecommerce: {
          item_list_name: itemData.item_list_name,
          items: [
            {
              item_id: itemData.item_id,
              item_name: itemData.item_name,
              price: itemData.price ? parseFloat(itemData.price) : undefined,
              item_brand: "Darwin's Natural Pet Products",
            },
          ],
        },
      });
    });
  }

  function initDigiohBridge() {
    // Option B: if Digioh pushes GTM events into `window.dataLayer`, bridge them.
    // This value comes from the `DigiohBoxName` field in the DigiohSubmit event.
    // Example: { event: 'DigiohSubmit', DigiohBoxName: 'Darwins Natural Pet Products Quiz' }
    const DIGIOH_CALCULATOR_NAME = 'Darwins Natural Pet Products Quiz';

    if (!window.dataLayer || !Array.isArray(window.dataLayer)) return;
    const originalPush = window.dataLayer.push.bind(window.dataLayer);

    window.dataLayer.push = function () {
      const result = originalPush.apply(this, arguments);
      const entry = arguments[0];

      if (!entry || entry.event !== 'DigiohSubmit') return result;
      if (entry.DigiohBoxName !== DIGIOH_CALCULATOR_NAME) return result;

      publishDarwinsEvent('darwins:calculator_used', { action: 'submit' });
      return result;
    };
  }

  function initDomEventBridge() {
    const selectorMap = [
      {
        // Pet type selector (dog/cat) - update selector if needed
        selector: '[data-pet-type]',
        eventName: 'darwins:pet_type_selected',
        getPayload: (el) => ({ pet_type: el.getAttribute('data-pet-type') }),
      },
      {
        // Subscribe/one-time toggle - update selector if needed
        selector: '[data-purchase-option]',
        eventName: 'darwins:purchase_option_selected',
        getPayload: (el) => ({ option: el.getAttribute('data-purchase-option') }),
      },
    ];

    document.addEventListener('click', (event) => {
      const target = event.target;

      selectorMap.forEach(({ selector, eventName, getPayload }) => {
        const el = target.closest(selector);
        if (!el) return;

        publishDarwinsEvent(eventName, getPayload(el));
      });
    });

    // Loop re-renders the purchase option widget as div[role="radio"] elements
    // that handle interactions internally — click events don't propagate and
    // [data-purchase-option] is stripped from the DOM. Watch aria-checked
    // mutations instead, which Loop always updates on selection.
    const knownState = new WeakMap();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        const el = mutation.target;
        if (!el.hasAttribute('data-loop-widget-purchase-option')) return;

        const isChecked = el.getAttribute('aria-checked') === 'true';

        if (!knownState.has(el)) {
          knownState.set(el, isChecked);
          return;
        }

        const wasChecked = knownState.get(el);
        knownState.set(el, isChecked);

        if (isChecked && !wasChecked) {
          const option = el.hasAttribute('data-loop-widget-onetime-purchase-option') ? 'one_time' : 'subscribe';
          publishDarwinsEvent('darwins:purchase_option_selected', { option });
        }
      });
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['aria-checked'],
      subtree: true,
    });
  }

  function init() {
    // Make the bridge resilient to pages where Shopify.analytics isn’t ready yet.
    if (!hasPublish()) {
      window.addEventListener('DOMContentLoaded', () => {
        // delay a tick to allow Shopify to initialize
        setTimeout(() => {
          initCustomEventBridge();
          initDigiohBridge();
          initDomEventBridge();
        }, 0);
      });
      return;
    }

    initCustomEventBridge();
    initDigiohBridge();
    initDomEventBridge();
  }

  init();
})();
